import { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { formatDuration } from "@/context/CallContext";
import { useAuth } from "@/context/AuthContext";
import EmptyState from "@/components/EmptyState";
import { Play, Pause, Microphone, Eye, Download, LockKey } from "@phosphor-icons/react";

export default function Recordings() {
  const [recs, setRecs] = useState([]);
  const [playing, setPlaying] = useState(null);       // recording id currently playing
  const [audioUrl, setAudioUrl] = useState(null);      // resolved storage URL
  const [loadingAudio, setLoadingAudio] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const audioRef = useRef(null);

  const { user } = useAuth();
  const isOversight = user?.role === "admin" || user?.role === "supervisor" || user?.role === "owner";
  const canDownload = isOversight;
  // Admin without their own Communication Seat cannot access personal
  // recordings — the "Mine" tab is hidden and any recording owned by the
  // admin is filtered out server-side view.
  const adminNoSeat = user?.role === "admin" && !user?.has_own_seat;

  // Tab state — default to Team for admin-without-seat (their only accessible
  // view), Mine otherwise.
  const defaultTab = adminNoSeat ? "team" : (isOversight ? "team" : "mine");
  const [tab, setTab] = useState(defaultTab);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/recordings");
      setRecs(data || []);
    })();
  }, []);

  // Partition rows by ownership. `owner_id` is stamped on each recording at
  // insert time (Twilio recording webhook / call.py).
  const { mine, team } = useMemo(() => {
    const mineArr = [];
    const teamArr = [];
    for (const r of recs) {
      if (r.owner_id === user?.id) mineArr.push(r);
      else teamArr.push(r);
    }
    return { mine: mineArr, team: teamArr };
  }, [recs, user?.id]);

  const visible = tab === "mine" ? mine : team;

  const playRecording = async (r) => {
    // Toggle off if already playing this row.
    if (playing === r.id) {
      audioRef.current?.pause();
      setPlaying(null);
      setAudioUrl(null);
      return;
    }
    // iter141: Play routes to /audio (the streaming proxy) instead of
    // /download (admin-only). Agents have LISTEN permission but not
    // DOWNLOAD — using /download here made agents receive
    //   "Agents are not allowed to download recordings. Listen-only access."
    // even though they were only trying to listen. The backend already
    // permits agents on /audio (see routes/calls.py — stream_recording_audio
    // explicitly documents "Agents CAN listen").
    //
    // Synthetic-demo detection is done client-side from the row we
    // already have — recordings with no `recording_url` and no
    // `storage_key` never carry audio bytes, so we short-circuit before
    // hitting the network to preserve the "demo recording" UX.
    const hasMedia = Boolean(r.recording_url || r.storage_key || r.media_url);
    if (!hasMedia || (r.duration ?? 0) === 0) {
      toast.info("This is a synthetic demo recording — no audio to play.");
      return;
    }
    setLoadingAudio(r.id);
    try {
      const audioSrc = `${process.env.REACT_APP_BACKEND_URL}/api/recordings/${r.id}/audio`;
      setAudioUrl(audioSrc);
      setPlaying(r.id);
      // Give the <audio> element a tick to mount before autoplay
      setTimeout(() => {
        audioRef.current?.play().catch((err) => {
          toast.error("Playback failed: " + (err?.message || "browser blocked audio"));
          setPlaying(null);
        });
      }, 50);
    } finally {
      setLoadingAudio(null);
    }
  };

  const downloadRecording = async (r) => {
    if (!canDownload) {
      toast.error("Agents are not allowed to download recordings. Listen-only access.");
      return;
    }
    setDownloading(r.id);
    try {
      const { data } = await api.get(`/recordings/${r.id}/download`);
      if (data.synthetic) {
        const blob = new Blob([data.content || ""], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = data.filename || `recording-${r.id}.txt`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("Transcript stub downloaded");
      } else if (data.download_url) {
        // iter134: same origin-prefix rule for the download click path.
        const dlHref = data.download_url.startsWith("/api/")
          ? `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`
          : data.download_url;
        const a = document.createElement("a");
        a.href = dlHref; a.download = data.filename || `recording-${r.id}.mp3`;
        a.target = "_blank";
        document.body.appendChild(a); a.click(); a.remove();
        toast.success("Recording download started");
      } else {
        toast.error("No download available for this recording");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="p-8" data-testid="recordings-page">
      <PageHeader
        eyebrow="VOICE ARCHIVE"
        title="Call recordings"
        subtitle={`${visible.length} recording${visible.length === 1 ? "" : "s"} in this view`}
        right={isOversight && (
          <div className="flex items-center gap-2 px-3 py-1.5 border border-[var(--border)] bg-white">
            <Eye size={14} className="text-[var(--accent)]" />
            <span className="font-mono text-[10px] tracking-widest">
              {tab === "team" ? "TEAM RECORDINGS" : "MY RECORDINGS"}
            </span>
          </div>
        )}
      />

      {/* Tab strip — only shown for oversight roles (admins/supervisors/owners).
          Admin without own_seat sees the Mine tab disabled with a lock. */}
      {isOversight && (
        <div className="flex items-center gap-1 mt-6 border-b border-[var(--border)]" data-testid="recordings-tabs">
          <TabBtn active={tab === "team"} onClick={() => setTab("team")}
                  count={team.length} testId="recordings-tab-team">
            Team recordings
          </TabBtn>
          {adminNoSeat ? (
            <span
              className="flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium text-[var(--text-muted)] cursor-not-allowed"
              title="Personal recordings require your own Communication Seat. Purchase a seat on the Numbers page to unlock this tab."
              data-testid="recordings-tab-mine-locked"
            >
              <LockKey size={13} /> My recordings
              <span className="text-[10px] uppercase tracking-widest ml-1">Seat required</span>
            </span>
          ) : (
            <TabBtn active={tab === "mine"} onClick={() => setTab("mine")}
                    count={mine.length} testId="recordings-tab-mine">
              My recordings
            </TabBtn>
          )}
        </div>
      )}

      {/* Hidden HTML5 audio node used for real playback. Positioned off-flow
          so the styled Play/Pause buttons drive it. */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={() => { setPlaying(null); setAudioUrl(null); }}
          onError={() => { toast.error("Audio failed to load"); setPlaying(null); setAudioUrl(null); }}
          data-testid="recordings-audio-player"
          controls
          className="fixed bottom-6 right-6 z-50 shadow-lg rounded"
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
        {visible.map((r) => (
          <div key={r.id} className="card p-5" data-testid={`recording-${r.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold text-[16px]">{r.call?.to_name || "Unknown"}</div>
                <div className="font-mono text-[12px] text-[var(--text-muted)]">{r.call?.to_number}</div>
                {isOversight && r.call?.agent_name && (
                  <div className="text-[11px] text-[var(--accent)] mt-1 font-medium">Agent: {r.call.agent_name}</div>
                )}
                <div className="font-mono text-[11px] text-[var(--text-muted)] mt-1">{new Date(r.created_at).toLocaleString()}</div>
              </div>
              <div className="font-mono text-[20px] font-medium">{formatDuration(r.duration)}</div>
            </div>

            {/* Waveform */}
            <div className="mt-4 flex items-end h-14 gap-[2px]">
              {(r.waveform || []).map((h, i) => (
                <div
                  key={`${r.id}-bar-${i}`}
                  className={`flex-1 ${playing === r.id ? "bg-[var(--accent)]" : "bg-[var(--text-muted)]"}`}
                  style={{ height: `${h}%`, opacity: playing === r.id ? 1 : 0.55 }}
                />
              ))}
            </div>

            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={() => playRecording(r)}
                disabled={loadingAudio === r.id}
                className="btn-primary flex items-center gap-2"
                data-testid={`play-${r.id}`}
              >
                {loadingAudio === r.id
                  ? "Loading…"
                  : playing === r.id
                    ? <><Pause size={14} weight="fill" /> Pause</>
                    : <><Play size={14} weight="fill" /> Play</>}
              </button>
              {canDownload ? (
                <button
                  onClick={() => downloadRecording(r)}
                  disabled={downloading === r.id}
                  className="btn-secondary flex items-center gap-2"
                  data-testid={`download-${r.id}`}
                >
                  <Download size={14} />
                  {downloading === r.id ? "Downloading…" : "Download"}
                </button>
              ) : (
                <span
                  className="text-[11px] font-mono tracking-widest text-[var(--text-muted)] flex items-center gap-1"
                  title="Listen-only — download is restricted to admins, supervisors and owners"
                  data-testid={`download-blocked-${r.id}`}
                >
                  LISTEN-ONLY
                </span>
              )}
              <div className="text-[12px] text-[var(--text-muted)] font-mono">
                {playing === r.id ? "PLAYING" : "READY"}
              </div>
              <div className="ml-auto flex items-center gap-1.5 text-[var(--accent)]">
                <Microphone size={14} />
                <span className="font-mono text-[11px] tracking-widest">RECORDED</span>
              </div>
            </div>
          </div>
        ))}
        {visible.length === 0 && (
          <div className="col-span-2 card">
            <EmptyState
              icon="recordings"
              title={tab === "team" ? "No team recordings yet" : "You have no personal recordings"}
              description={
                tab === "team"
                  ? "As your team records calls, they'll appear here for you to review, listen and download."
                  : 'Toggle "Record" during an active call to capture it. Personal recordings appear here.'
              }
              primaryAction={adminNoSeat ? undefined : { label: "Open dialer", onClick: () => (window.location.href = "/app/dialer") }}
              learnMore={{ label: "Recording policies", href: "/app/settings" }}
              testId="empty-state-recordings"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, count, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2.5 text-[13px] font-medium transition-colors border-b-2 -mb-px ${
        active
          ? "border-[var(--accent)] text-[var(--text-primary)]"
          : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]"
      }`}
    >
      {children}
      {typeof count === "number" && (
        <span className={`ml-2 text-[11px] font-mono ${active ? "text-[var(--accent)]" : "text-[var(--text-muted)]"}`}>
          {count}
        </span>
      )}
    </button>
  );
}
